export const salesPageData = {
  stats: [
    { value: "3×", label: "Sales Productivity" },
    { value: "99%+", label: "Order Accuracy" },
    { value: "60%", label: "Less Admin Time" },
    { value: "Real-Time", label: "Team Performance Visibility" },
  ],
  statsAccent: "from-violet-600 to-purple-700",

  challengeTitle: "Your Sales Team Is Spending 60% of Their Time Not Selling",
  challengeSubtitle:
    "Manual order entry, guesswork pricing, and inefficient routes are crippling your field sales performance.",
  challenges: [
    {
      icon: "⏰",
      title: "Hours Lost to Manual Order Entry",
      description:
        "Reps spend hours typing orders, checking prices, and following up instead of building relationships and closing deals.",
    },
    {
      icon: "💸",
      title: "Negotiating Without Pricing Data",
      description:
        "Reps quote from memory or outdated price lists — losing deals to competitors who know the market price.",
    },
    {
      icon: "🗺️",
      title: "Inefficient Territory Coverage",
      description:
        "Without route optimisation, reps visit customers in the wrong sequence — wasting fuel and reducing daily visit counts.",
    },
    {
      icon: "📊",
      title: "Managers Flying Blind",
      description:
        "Sales managers can't see daily order activity, team performance, or territory gaps without end-of-day reports.",
    },
    {
      icon: "❌",
      title: "High Order Error Rate",
      description:
        "Manual order entry produces errors that delay fulfilment, damage customer trust, and generate costly credit notes.",
    },
    {
      icon: "📉",
      title: "No Customer Visit Intelligence",
      description:
        "Without visit logs and customer insights, reps arrive unprepared — missing opportunities to upsell or resolve issues.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-violet-50 text-violet-700 border-violet-100",
    solutionIconBg: "bg-violet-50",
    solutionIconColor: "text-violet-600",
    solutionAccent: "from-violet-600 to-purple-600",
  },

  solutionTitle: "AI Sales Force Automation That Triples Productivity",
  solutionSubtitle:
    "Empower every sales rep with AI-powered ordering, real-time pricing intelligence, and smart route planning.",
  solutions: [
    {
      icon: "🎤",
      title: "AI Voice Ordering",
      description:
        "Reps place orders by speaking naturally — AI interprets, matches to SKUs, confirms pricing, and submits instantly.",
    },
    {
      icon: "📸",
      title: "Photo-to-Order AI",
      description:
        "Photograph a product or shelf — AI identifies every item, creates the order, and submits it with one tap.",
    },
    {
      icon: "💰",
      title: "Real-Time Pricing Intelligence",
      description:
        "Live competitive prices from multiple suppliers mean every rep always quotes confidently and wins more deals.",
    },
    {
      icon: "🗺️",
      title: "Smart Route Planning",
      description:
        "AI-optimised daily visit routes maximise customer coverage, reduce travel time, and increase daily call rates.",
    },
    {
      icon: "📊",
      title: "Live Performance Dashboard",
      description:
        "Sales managers see every rep's orders, visit count, revenue, and territory coverage updated in real time.",
    },
    {
      icon: "🚚",
      title: "Auto Driver & Collector Assignment",
      description:
        "Orders are automatically assigned to delivery drivers and cash collectors — no manual coordination needed.",
    },
    {
      icon: "🎯",
      title: "Customer Visit Intelligence",
      description:
        "Full customer history, outstanding balances, and AI-recommended talking points ready before every visit.",
    },
    {
      icon: "📱",
      title: "Mobile-First Field App",
      description:
        "Purpose-built mobile app works offline and online — so reps are productive even in low-connectivity areas.",
    },
    {
      icon: "📈",
      title: "Sales Forecasting",
      description:
        "AI forecasts territory-level revenue based on visit patterns, order history, and market signals — helping managers set realistic targets.",
    },
  ],

  howItWorksTitle: "Field Sales Transformation in 4 Steps",
  howItWorksSteps: [
    {
      icon: "📱",
      title: "Rep Opens the App",
      description: "The mobile app shows today's optimised route, customer priorities, and visit objectives.",
    },
    {
      icon: "🎤",
      title: "Voice or Photo Order",
      description: "Rep places the order by speaking or photographing products — no manual typing required.",
    },
    {
      icon: "⚡",
      title: "Order Processed Instantly",
      description: "AI confirms pricing, submits the order, and assigns delivery and collection automatically.",
    },
    {
      icon: "📊",
      title: "Manager Sees It Live",
      description: "Every order appears on the manager's dashboard in real time — full visibility, no end-of-day reports.",
    },
  ],

  outcomeTitle: "Sales Performance That Speaks for Itself",
  outcomeSubtitle: "What companies achieve after deploying EkkBaz AI Sales Force Automation.",
  outcomes: [
    {
      value: "3×",
      metric: "Sales Productivity",
      description:
        "Reps close 3× more orders per day when freed from manual data entry — spending their time selling, not typing.",
    },
    {
      value: "99%+",
      metric: "Order Accuracy",
      description:
        "AI-powered order capture eliminates manual entry errors — reducing credit notes and rework to near zero.",
    },
    {
      value: "60%",
      metric: "Less Admin Time",
      description:
        "Voice and photo ordering eliminates 60% of daily administrative work — giving every rep back hours to sell.",
    },
  ],
  outcomeImage: "/assets/images/products/sales2.png",

  ctaTitle: "Triple Your Sales Team's Productivity with AI",
  ctaSubtitle:
    "Book a free consultation to see how EkkBaz AI can transform your field sales team's performance — starting this quarter.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const salesData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Sales Teams Are Spending More Time on Admin Than Selling",
    description:
      "Your sales reps waste 60% of their time on manual order entry, route planning, and data collection — time that should be spent building relationships and closing deals. Without real-time market pricing data, your team negotiates blind and loses deals to better-informed competitors.",
    banner: "/assets/images/products/sales1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "AI-Powered Sales Force Automation for Field and Inside Sales Teams",
    description:
      "EkkBaz AI transforms how sales teams operate. Field reps can place orders using AI voice commands or simply by photographing products — eliminating manual data entry entirely. The platform shows competitive pricing from multiple suppliers in real time, assigns delivery drivers and collectors automatically, and gives sales managers a live dashboard of every rep's daily orders, coverage, and performance.",
    banner: "/assets/images/products/sales2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Increase Sales Productivity 3× with AI Sales Intelligence",
    description:
      "EkkBaz AI sales force automation increases rep productivity by 3×, boosts order accuracy to 99%+, and gives sales managers real-time visibility into team performance, territory coverage, and revenue attainment. AI pricing intelligence ensures every rep always offers the most competitive price — winning more deals and protecting margins simultaneously.",
    banner: "/assets/images/products/sales3.png",
  },
];

export const salesFAQs = [
  {
    question: "How does EkkBaz AI voice ordering work for sales reps?",
    answer:
      "EkkBaz AI enables field sales reps to place orders by speaking naturally or photographing products. The AI interprets the input, matches it to the correct SKU, confirms pricing, and submits the order digitally — eliminating manual data entry and order errors entirely.",
  },
  {
    question: "How does EkkBaz AI provide competitive pricing intelligence for sales teams?",
    answer:
      "EkkBaz AI aggregates real-time pricing from multiple suppliers on the platform and surfaces the most competitive available prices to sales reps during the ordering process — ensuring your team always offers market-competitive pricing to close deals faster.",
  },
  {
    question: "Can EkkBaz AI track sales rep performance and territory coverage?",
    answer:
      "Yes. EkkBaz AI provides sales managers with a live performance dashboard showing each rep's daily orders, visit counts, revenue attainment, and geographic coverage — enabling data-driven coaching and territory optimization.",
  },
];
